import { InvitesHttpService } from "@/modules/candidate/services/InvitesHttpService";
import { LanguagesIsoEnum } from "@/modules/assessments/types/test.interface";
import type { GetDemographicsResponse } from "./demographics.types";

const preloaded = new Map<string, GetDemographicsResponse>();

const keyFor = (inviteId: number, locale?: LanguagesIsoEnum) => `${inviteId}:${locale ?? ""}`;

/**
 * Loads an invite's demographic questions in a locale ahead of showing them, so the labels can change
 * in the same render as the rest of the page instead of after their own request.
 *
 * @param {number} inviteId - invite whose questions to load
 * @param {LanguagesIsoEnum} locale - locale to resolve the labels to
 * @returns {Promise<void>} settles once the request has; a failure resolves too, leaving the form to
 *   request the labels itself
 */
export async function preloadDemographics(inviteId: number, locale: LanguagesIsoEnum): Promise<void> {
  try {
    preloaded.set(keyFor(inviteId, locale), await InvitesHttpService.getDemographicsOptions(inviteId, locale));
  } catch {
    preloaded.delete(keyFor(inviteId, locale));
  }
}

/**
 * Hands over a finished preload once, so a later read of the same locale fetches fresh labels.
 *
 * @param {number} inviteId - invite whose questions were preloaded
 * @param {LanguagesIsoEnum} [locale] - locale they were resolved to
 * @returns {GetDemographicsResponse | undefined} the preloaded response, if one is waiting
 */
export function takePreloadedDemographics(
  inviteId: number,
  locale?: LanguagesIsoEnum,
): GetDemographicsResponse | undefined {
  const key = keyFor(inviteId, locale);
  const response = preloaded.get(key);
  preloaded.delete(key);
  return response;
}
